import { useCallback, useEffect, useRef } from 'react';

import { useScopeInfo } from '~/contexts';
import { useQueryApi, UseQueryApiResponse, useRumDurationVital } from '~/hooks';

import { NullableSearchResults, UseSearchFormResultsParams } from './types';
import { executeProviderSearch, useResultsCache } from './utils';

export function useSearchFormResults({
  formData,
  showAdditionalProviders,
}: UseSearchFormResultsParams): Pick<UseQueryApiResponse<NullableSearchResults>, 'data' | 'loading'> {
  const { getCacheValue, setCacheValue } = useResultsCache();
  const scopeInfo = useScopeInfo();
  if (!scopeInfo) throw new Error('No scope info in app state');
  const { plan, isDemo } = scopeInfo;

  // Time each provider search from submit to results. RUM resource timing already covers the
  // underlying network call; this vital captures the full user-perceived span (cache lookup +
  // request + result processing).
  const { startVital, stopVital } = useRumDurationVital({ startOnMount: false, vitalName: 'provider_search' });

  // Correlate timing with the specific search that owns the vital. useQueryApi shares a single
  // `loading` flag and does not cancel stale requests, so an earlier search resolving late could
  // otherwise stop a newer search's vital and record its duration against the wrong request. Each
  // search bumps this generation; only the search that started the current vital may stop it.
  const searchGenerationRef = useRef(0);

  const search = useCallback(async () => {
    const generation = (searchGenerationRef.current += 1);
    // Restart timing so the measurement always tracks the current submission.
    stopVital();
    startVital();
    try {
      return await executeProviderSearch({
        formData,
        showAdditionalProviders,
        plan,
        isDemo,
        getCacheValue,
        setCacheValue,
      });
    } finally {
      if (generation === searchGenerationRef.current) stopVital();
    }
    // Key only on formData and scope: the cache helpers are re-created each render (keying on them would
    // re-run every search) and startVital/stopVital are stable.
  }, [formData, plan, isDemo]);

  const { data, loading, query } = useQueryApi<NullableSearchResults>(search, true);

  useEffect(query, [formData]);

  return { data, loading };
}
